import type { Ref } from "vue";
import { sourceData } from "~/plugins/downsub";
import { parseISO8601DurationSeconds } from "~/utils/tools";

export type YoutubeVideoInfo = { durationSeconds: number; title: string };

// Video info per YouTube id for the page's lifetime.
const cache = new Map<string, YoutubeVideoInfo>();

export const extractYoutubeId = (url: string): string | null => {
  const match = `${url || ""}`.trim().match(sourceData[0].regex);
  return match?.[1] || null;
};

/**
 * Looks up a YouTube video's duration (seconds) and title via
 * youtube-checker; zeros/empty when the lookup fails. Results are cached for
 * the page's lifetime. `encode` is the `$encode` helper from the downsub
 * plugin.
 */
export const fetchYoutubeVideoInfo = async (
  videoId: string,
  encode: (value: string) => string | false,
  signal?: AbortSignal
): Promise<YoutubeVideoInfo> => {
  const empty = { durationSeconds: 0, title: "" };
  if (!videoId) return empty;
  if (cache.has(videoId)) return cache.get(videoId)!;
  try {
    const encoded = encode(JSON.stringify({ videoId, timestamp: Date.now() }));
    if (!encoded) return empty;
    const response = await fetch(
      `https://youtube-checker.downsub.com/${encoded}`,
      { signal }
    );
    if (!response.ok) return empty;
    const body = await response.json();
    const item = body?.items?.[0];
    const info = {
      durationSeconds: parseISO8601DurationSeconds(item?.contentDetails?.duration || ""),
      title: `${item?.snippet?.title || ""}`.trim(),
    };
    if (info.durationSeconds > 0 || info.title) cache.set(videoId, info);
    return info;
  } catch {
    // Lookup is best effort; the server still checks credits.
    return empty;
  }
};

/** Duration-only shorthand of `fetchYoutubeVideoInfo`. */
export const fetchYoutubeDurationSeconds = async (
  videoId: string,
  encode: (value: string) => string | false,
  signal?: AbortSignal
): Promise<number> => (await fetchYoutubeVideoInfo(videoId, encode, signal)).durationSeconds;

/**
 * Duration (seconds) of a pasted YouTube link, looked up before the user
 * starts a job so the credit estimate is known up front. Uses the same
 * youtube-checker service as the YouTube tools page. Non-YouTube links and
 * failed lookups give 0 (the caller then falls back to the server's figure).
 */
export const useYoutubeDuration = (url: Ref<string>, debounceMs = 400) => {
  const { $encode } = useNuxtApp();
  const durationSeconds = ref(0);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let requestSeq = 0;

  const lookup = async (videoId: string, seq: number) => {
    const seconds = await fetchYoutubeDurationSeconds(videoId, $encode);
    if (seq === requestSeq) durationSeconds.value = seconds;
  };

  watch(
    url,
    (value) => {
      clearTimeout(timer);
      const seq = ++requestSeq;
      const videoId = extractYoutubeId(value);
      if (!videoId) {
        durationSeconds.value = 0;
        return;
      }
      if (cache.has(videoId)) {
        durationSeconds.value = cache.get(videoId)!.durationSeconds;
        return;
      }
      durationSeconds.value = 0;
      if (!import.meta.client) return;
      timer = setTimeout(() => void lookup(videoId, seq), debounceMs);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => clearTimeout(timer));

  return { durationSeconds };
};
